import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ethers } from 'ethers';

import Alert from './Alert';
import { swap, loadBalances } from '../store/interactions';
import { connectProvider } from '../store/reducers/provider';
import { getNetworkConfig, getNetworkLabel, SEPOLIA_CHAIN_ID } from '../utils/network';
import { formatRate, shortenAddress, friendlyError } from '../utils/format';
import AMM_ABI from '../abis/AMM.json';
import Aggregator_ABI from '../abis/Aggregator.json';
import TOKEN_ABI from '../abis/Token.json';

const Swap = () => {
  const dispatch = useDispatch();

  const [inputToken, setInputToken] = useState('');
  const [outputToken, setOutputToken] = useState('');
  const [inputAmount, setInputAmount] = useState('');
  const [outputAmount, setOutputAmount] = useState('');
  const [price, setPrice] = useState(0);
  const [bestAmm, setBestAmm] = useState('');
  const [priceFromAmm1, setPriceFromAmm1] = useState('0');
  const [priceFromAmm2, setPriceFromAmm2] = useState('0');
  const [showAlert, setShowAlert] = useState(false);
  const [userMessage, setUserMessage] = useState('');

  const provider = useSelector((state) => state.provider.connection);
  const account = useSelector((state) => state.provider.account);
  const chainId = useSelector((state) => state.provider.chainId);
  const tokens = useSelector((state) => state.tokens.contracts);
  const loadingConnect = useSelector((state) => state.provider.loading);

  const isSwapping = useSelector((state) => state.amm.swapping?.isSwapping);
  const isSuccess = useSelector((state) => state.amm.swapping?.isSuccess);
  const transactionHash = useSelector((state) => state.amm.swapping?.transactionHash);

  const networkConfig = useMemo(() => getNetworkConfig(chainId), [chainId]);

  const amm1Contract = useMemo(() => {
    if (!networkConfig || !provider) return null;
    return new ethers.Contract(networkConfig.amm1.ammAddress, AMM_ABI, provider);
  }, [networkConfig, provider]);

  const amm2Contract = useMemo(() => {
    if (!networkConfig || !provider) return null;
    return new ethers.Contract(networkConfig.amm2.ammAddress, AMM_ABI, provider);
  }, [networkConfig, provider]);

  const aggregatorContract = useMemo(() => {
    if (!networkConfig || !provider) return null;
    return new ethers.Contract(networkConfig.aggregator.address, Aggregator_ABI, provider);
  }, [networkConfig, provider]);

  const resolveTokenAddress = useCallback(
    (symbol) => {
      if (symbol === 'DAPP') return tokens.dappToken1Address;
      if (symbol === 'USD') return tokens.usdToken1Address;
      return null;
    },
    [tokens]
  );

  const refreshQuotes = useCallback(async () => {
    if (!amm1Contract || !amm2Contract || !aggregatorContract) return;
    if (!inputToken || !outputToken) return;

    const tokenInAddress = resolveTokenAddress(inputToken);
    const tokenOutAddress = resolveTokenAddress(outputToken);
    if (!tokenInAddress || !tokenOutAddress) return;

    try {
      let p1 = null;
      let p2 = null;
      try {
        p1 = await amm1Contract.getPrice(tokenInAddress, tokenOutAddress);
        setPriceFromAmm1(ethers.utils.formatUnits(p1, 18));
      } catch {
        setPriceFromAmm1('0');
      }
      try {
        p2 = await amm2Contract.getPrice(tokenInAddress, tokenOutAddress);
        setPriceFromAmm2(ethers.utils.formatUnits(p2, 18));
      } catch {
        setPriceFromAmm2('0');
      }

      const [bestPriceBN, selected] = await aggregatorContract.getPriceData(
        tokenInAddress,
        tokenOutAddress
      );
      setBestAmm(selected);
      const parsed = parseFloat(ethers.utils.formatUnits(bestPriceBN, 18));
      setPrice(parsed);
      setUserMessage('');
    } catch (error) {
      console.error('Quote error:', error);
      setPrice(0);
      setBestAmm('');
      setUserMessage(friendlyError(error, 'No liquidity available for this pair.'));
    }
  }, [
    amm1Contract,
    amm2Contract,
    aggregatorContract,
    inputToken,
    outputToken,
    resolveTokenAddress,
  ]);

  useEffect(() => {
    refreshQuotes();
  }, [refreshQuotes]);

  const calcOutput = (val) => {
    if (!price || isNaN(price)) {
      setOutputAmount('');
      return;
    }
    const res = parseFloat(val) * price;
    setOutputAmount(res ? res.toFixed(6) : '0');
  };

  const inputHandler = (e) => {
    setInputAmount(e.target.value);
    calcOutput(e.target.value);
  };

  const swapTokensDirection = () => {
    const nextIn = outputToken;
    const nextOut = inputToken;
    setInputToken(nextIn);
    setOutputToken(nextOut);
    setInputAmount('');
    setOutputAmount('');
  };

  const swapHandler = async (e) => {
    e.preventDefault();
    setShowAlert(false);
    setUserMessage('');

    if (!provider || !account) {
      setUserMessage('Connect your wallet to continue.');
      return;
    }
    if (!networkConfig || !amm1Contract || !amm2Contract || !aggregatorContract) {
      setUserMessage('Network contracts are not available.');
      return;
    }
    if (!inputToken || !outputToken || parseFloat(inputAmount) <= 0) {
      setUserMessage('Select tokens and enter an amount.');
      return;
    }
    if (inputToken === outputToken) {
      setUserMessage('Input and output tokens must differ.');
      return;
    }

    const tokenInAddress = resolveTokenAddress(inputToken);
    const tokenOutAddress = resolveTokenAddress(outputToken);
    if (!tokenInAddress || !tokenOutAddress) {
      setUserMessage('Invalid token addresses for this network.');
      return;
    }

    try {
      const inputAmountWei = ethers.utils.parseUnits(inputAmount, 'ether');
      const tokenIn = new ethers.Contract(tokenInAddress, TOKEN_ABI, provider);
      const tokenOut = new ethers.Contract(tokenOutAddress, TOKEN_ABI, provider);

      await swap(
        provider,
        {
          aggregatorContract,
          amm1Contract,
          amm2Contract,
          tokenIn,
          tokenOut,
          amountIn: inputAmountWei,
        },
        dispatch
      );

      await loadBalances(provider, networkConfig, dispatch);
      await refreshQuotes();
      setShowAlert(true);
    } catch (error) {
      console.error('Swap error:', error);
      setUserMessage(friendlyError(error, 'Transaction failed.'));
      setShowAlert(true);
    }
  };

  const ctaLabel = (() => {
    if (!account) return loadingConnect ? 'Connecting…' : 'Connect Wallet';
    if (!inputToken || !outputToken) return 'Select Tokens';
    if (!inputAmount || parseFloat(inputAmount) <= 0) return 'Enter Amount';
    if (isSwapping) return 'Swapping…';
    return 'Swap';
  })();

  const ctaDisabled = (() => {
    if (!account) return loadingConnect;
    if (isSwapping) return true;
    if (!inputToken || !outputToken) return true;
    if (!inputAmount || parseFloat(inputAmount) <= 0) return true;
    if (inputToken === outputToken) return true;
    return false;
  })();

  const onCtaClick = (e) => {
    if (!account) {
      e.preventDefault();
      if (window.ethereum) dispatch(connectProvider());
      return;
    }
  };

  const pairLabel =
    inputToken && outputToken ? `${outputToken} / ${inputToken}` : 'Select pair';

  const estimatedRate =
    inputToken && outputToken && price
      ? `1 ${inputToken} → ${formatRate(price, 4)} ${outputToken}`
      : '—';

  if (!networkConfig) {
    return (
      <div className="card state-panel">
        <h2>Connect a network</h2>
        <p>Switch to Sepolia or Localhost 31337 to use Swap.</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 'var(--content-max)', margin: '0 auto' }}>
      <section className="card card--elevated" aria-labelledby="swap-title">
        <p className="card__eyebrow">DEX Aggregator</p>
        <h1 id="swap-title" className="card__title">
          Swap
        </h1>
        <p className="card__subtitle">
          Compare liquidity across two AMMs and execute through the selected route.
        </p>

        {Number(chainId) === SEPOLIA_CHAIN_ID && account ? (
          <aside className="demo-notice demo-notice--compact" aria-label="Network demo notice">
            <p className="demo-notice__title">Sepolia Testnet Demo · Test ETH only</p>
          </aside>
        ) : (
          <aside className="demo-notice" aria-label="Network demo notice">
            <p className="demo-notice__title">Sepolia Testnet Demo</p>
            <p className="demo-notice__body">
              Connect MetaMask and switch to Sepolia. Test ETH is only required for transactions.
            </p>
          </aside>
        )}

        <form onSubmit={swapHandler}>
          <div className="token-panel">
            <div className="token-panel__top">
              <span className="token-panel__label">You pay</span>
            </div>
            <div className="token-panel__row">
              <input
                className="token-panel__amount"
                type="number"
                inputMode="decimal"
                min="0"
                step="any"
                placeholder="0.0"
                aria-label="Input amount"
                value={inputAmount}
                onChange={inputHandler}
                disabled={!inputToken}
              />
              <select
                className="token-select"
                aria-label="Input token"
                value={inputToken}
                onChange={(e) => {
                  setInputToken(e.target.value);
                  setInputAmount('');
                  setOutputAmount('');
                }}
              >
                <option value="">Token</option>
                <option value="DAPP">DAPP</option>
                <option value="USD">USD</option>
              </select>
            </div>
          </div>

          <div className="swap-arrow">
            <button
              type="button"
              className="swap-arrow__btn"
              aria-label="Switch tokens"
              onClick={swapTokensDirection}
              disabled={!inputToken && !outputToken}
            >
              ↓
            </button>
          </div>

          <div className="token-panel">
            <div className="token-panel__top">
              <span className="token-panel__label">You receive (estimated)</span>
            </div>
            <div className="token-panel__row">
              <input
                className="token-panel__amount"
                type="text"
                readOnly
                tabIndex={-1}
                placeholder="0.0"
                aria-label="Estimated output amount"
                value={outputAmount}
                title={outputAmount || undefined}
              />
              <select
                className="token-select"
                aria-label="Output token"
                value={outputToken}
                onChange={(e) => {
                  setOutputToken(e.target.value);
                  setOutputAmount('');
                }}
              >
                <option value="">Token</option>
                <option value="DAPP">DAPP</option>
                <option value="USD">USD</option>
              </select>
            </div>
          </div>

          <div className="amm-grid" role="group" aria-label="AMM price comparison">
            <article
              className={`amm-card${bestAmm === 'AMM1' ? ' is-best' : ''}`}
            >
              <div className="amm-card__head">
                <h3 className="amm-card__name">AMM 1</h3>
                {bestAmm === 'AMM1' && <span className="amm-card__badge">Selected</span>}
              </div>
              <p className="amm-card__price" title={priceFromAmm1}>
                {formatRate(priceFromAmm1, 4)}
              </p>
              <p className="amm-card__pair">{pairLabel}</p>
              <p className="amm-card__addr" title={networkConfig.amm1.ammAddress}>
                {shortenAddress(networkConfig.amm1.ammAddress)}
              </p>
            </article>

            <article
              className={`amm-card${bestAmm === 'AMM2' ? ' is-best' : ''}`}
            >
              <div className="amm-card__head">
                <h3 className="amm-card__name">AMM 2</h3>
                {bestAmm === 'AMM2' && <span className="amm-card__badge">Selected</span>}
              </div>
              <p className="amm-card__price" title={priceFromAmm2}>
                {formatRate(priceFromAmm2, 4)}
              </p>
              <p className="amm-card__pair">{pairLabel}</p>
              <p className="amm-card__addr" title={networkConfig.amm2.ammAddress}>
                {shortenAddress(networkConfig.amm2.ammAddress)}
              </p>
            </article>
          </div>

          <div className="route-summary" aria-label="Selected route summary">
            <div className="route-summary__item">
              <span className="route-summary__label">Selected Route</span>
              <span className="route-summary__value route-summary__value--accent">
                {bestAmm ? bestAmm.replace('AMM', 'AMM ') : '—'}
              </span>
            </div>
            <div className="route-summary__item">
              <span className="route-summary__label">Estimated rate</span>
              <span className="route-summary__value" title={price ? String(price) : undefined}>
                {estimatedRate}
              </span>
            </div>
            <div className="route-summary__item">
              <span className="route-summary__label">Execution</span>
              <span className="route-summary__value">Single-hop</span>
            </div>
            <div className="route-summary__item">
              <span className="route-summary__label">Network</span>
              <span className="route-summary__value">{getNetworkLabel(chainId)}</span>
            </div>
          </div>

          {userMessage && !showAlert && (
            <p className="helper-text" role="status" style={{ color: 'var(--warning)' }}>
              {userMessage}
            </p>
          )}

          <div className="btn-row">
            <button
              type={account ? 'submit' : 'button'}
              className="btn btn--primary"
              disabled={ctaDisabled}
              onClick={onCtaClick}
              aria-busy={isSwapping}
            >
              {isSwapping && <span className="spinner" aria-hidden="true" />}
              {ctaLabel}
            </button>
          </div>
        </form>
      </section>

      {isSwapping ? (
        <Alert
          message="Swapping…"
          transactionHash={null}
          variant="info"
          setShowAlert={setShowAlert}
        />
      ) : isSuccess && showAlert ? (
        <Alert
          message="Transaction Confirmed"
          transactionHash={transactionHash}
          variant="success"
          setShowAlert={setShowAlert}
        />
      ) : !isSuccess && showAlert ? (
        <Alert
          message={userMessage || 'Transaction Failed'}
          transactionHash={null}
          variant="danger"
          setShowAlert={setShowAlert}
        />
      ) : null}
    </div>
  );
};

export default Swap;
